import React, {
  useState,
  useCallback,
  memo,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import SearchInput from 'shared/components/SearchInput';
import {
  useSearch,
  queryToSearch,
  searchToQuery,
} from 'shared/components/makeNav';
import Popover from '@mui/material/Popover';
import omit from 'lodash/omit';
import without from 'lodash/without';
import NativeListener from 'react-native-listener';
import styled from 'styled-components';
import { Button } from 'shared/components';
import isEqual from 'lodash/isEqual';
import castArray from 'lodash/castArray';

import { useLocation, useHistory, Link } from 'react-router-dom';
import NavValue from 'shared/components/NavValue';
import DropDownMenu from 'shared/components/DropDownMenu';
import { groupBy } from 'shared/components/tables';
import { Row } from 'shared/components/styled';
import MenuItem from 'shared/components/MenuItem';
import Checkmark from 'shared/components/Checkmark';

export default function FilterDropDown({
  name,
  buttonTitle,
  flex,
  field,
  rows,
  onValue,
  sorted,
  keyId,
  width,
  buttonTextColor,
  noButtonBorder,
}) {
  const handleValue = useCallback(
    value => {
      onValue(value, name);
    },
    [name, onValue],
  );
  const search = useSearch();
  const value = search[name];
  const valueAsArray = useMemo(() => tryParse(value).sort(), [value]);
  const { pathname } = useLocation();

  const [filter, setFilter] = useState('');
  const groups = useMemo(() => {
    const result =
      rows &&
      groupBy(rows, field)
        .filter(({ key }) => key)
        .filter(
          ({ key }) =>
            !filter ||
            String(key)
              .toLowerCase()
              .includes(filter.toLowerCase()),
        );
    if (sorted && result) {
      if (keyId) {
        result.sort((a, b) =>
          !a.key ? 1 : !b.key ? -1 : keyId(a.key) - keyId(b.key),
        );
      } else {
        result.sort((a, b) =>
          !a.key ? 1 : !b.key ? -1 : String(a.key).localeCompare(b.key),
        );
      }
    }

    return result;
  }, [JSON.stringify(rows), field, sorted, keyId, filter]);

  const { push } = useHistory();
  const to = `${pathname}?${queryToSearch(omit(search, name))}`;
  const handleSearchClick = useCallback(event => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleSearchChange = useCallback(event => {
    setFilter(event.target.value);
  }, []);
  const [newFilters, setNewFilters] = useState(() => valueAsArray);
  const updateFilter = useCallback(() => {
    const query = searchToQuery(window.location.search);
    const { pathname } = window.location;
    const value = encodeURIComponent(JSON.stringify(newFilters));
    push(
      `${pathname}?${queryToSearch({ ...encodeObject(query), [name]: value })}`,
    );
  }, [newFilters, name, push]);
  const handleMenuItemClick = useCallback(event => {
    const { id } = event.target;
    setNewFilters(filters => {
      const result = castArray(
        filters.includes(id) ? without(filters, id) : [...filters, id],
      ).sort();
      console.log('handleMenuItemClick', event.target, filters, result);
      return result;
    });
  }, []);
  useEffect(() => setNewFilters(valueAsArray), [valueAsArray]);
  //  useEffect(() => updateFilter, [newFilters])
  const handleClearSelection = useCallback(() => {
    push(to);
  }, [push, to]);
  const items = groups?.length ? ( //useMemo(() =>
    groups.map(({ key, values }) => (
      <FilterMenuItem
        {...{ key }}
        value={key}
        onClick={handleMenuItemClick}
        checked={newFilters.includes(String(key))}
      >
        {key} ({values?.length})
      </FilterMenuItem>
    ))
  ) : (
    <NoItems>No items to display</NoItems>
  );
  //  , [groups, valueAsArray, handleMenuItemClick, newFilters]
  const menu = useRef();
  const handleApplyClick = useCallback(() => {
    updateFilter();
    menu.current.close();
  }, [updateFilter, menu.current]);

  return (
    <NavValue {...{ name }} value="*" onValue={handleValue}>
      {({ match }) => (
        <SelectableDropDownMenu
          {...{ buttonTitle, width, buttonTextColor, noButtonBorder, flex }}
          color="inherit"
          variant="outlined"
          selected={match}
          menuComponent={Popover}
          keepOpen
          ref={menu}
        >
          <SearchBar>
            <NativeListener onClick={handleSearchClick}>
              <SearchInput
                value={filter}
                onChange={handleSearchChange}
                fullWidth
              />
            </NativeListener>
            <ApplyButton
              disabled={isEqual(valueAsArray, newFilters)}
              onClick={handleApplyClick}
            >
              Apply
            </ApplyButton>
            {/*<MenuItem onClick={handleClearSelection}>
              Clear Selection
            </MenuItem>*
            <Spacer/>*/}
          </SearchBar>
          <Scroller>{items}</Scroller>
        </SelectableDropDownMenu>
      )}
    </NavValue>
  );
}

export const SelectableDropDownMenu = styled(DropDownMenu)`
  color: rgba(0,0,0,.38) !important;
  ${({ selected }) =>
    selected && 'background-color: rgba(0,0,0,.2) !important;'}
  ${({ width }) => width && `min-width: ${width}px !important;`}
  ${({ buttonTextColor }) =>
    buttonTextColor && `color: ${buttonTextColor} !important;`}
  ${({ noButtonBorder }) => noButtonBorder && 'border: 0 !important;'}
  ${({ flex }) => flex && 'flex: 1;'}
  text-transform: none !important;
  border-left: 0 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-color: #ddd !important;
`;

const SearchBar = styled(Row)`
  margin-top: -8px;
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const StayOnTop = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-ftiler: blur(5px);
`;

const NoItems = styled.div`
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
`;

const Spacer = styled.div`
  width: 50px;
`;

const Scroller = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
`;

const ApplyButton = styled(Button)`
  margin-top: 2px;
  height: 40px;
`;

function tryParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return [];
  }
}

const FilterMenuItem = memo(({ value, checked, children, ...other }) => (
  <MenuItem id={value} {...other}>
    <Checkmark {...{ checked }} id={value} /> {children}
  </MenuItem>
));

const encodeObject = obj =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, encodeURIComponent(value)]),
  );
